import type { Ref } from "vue";
import { formatDate } from "@/composables/formatUtils";
import { BeneficiaryTypeEnum } from "@/types";
import {
  BUY_ELIGIBLE_TECHNOLOGIES,
  type BuyEligibleTechnology,
} from "@/types/technology";

export type BeneficiaryType = BeneficiaryTypeEnum;
export const BeneficiaryType = BeneficiaryTypeEnum;

export enum PurchaseMode {
  CHEAPEST = "CHEAPEST",
  GHG = "GHG",
  RE100 = "RE100",
  ADVANCED = "ADVANCED",
  OTHER = "OTHER",
}

export enum EACSource {
  ANY = "ANY",
  STANDARD_AIB = "STANDARD_AIB",
  AIB_GRID_CONNECTED = "AIB_GRID_CONNECTED",
  AIB_GRID_CONNECTED_NO_NORWAY_ICELAND = "AIB_GRID_CONNECTED_NO_NORWAY_ICELAND",
  // Non-AIB schemes: quoting routes to an RFQ handled by our team (no automatic pricing).
  REGO = "REGO",
  IREC = "IREC",
}

// Buy-domain composites; atomic technologies derive from the registry's buyEligible flag.
const TECHNOLOGY_GROUPS = ["ANY", "HYDRO_WIND_SOLAR", "WIND_SOLAR"] as const;

export type Technology =
  (typeof TECHNOLOGY_GROUPS)[number] | Uppercase<BuyEligibleTechnology>;

export const BUY_TECHNOLOGY_VALUES: readonly Technology[] = [
  ...TECHNOLOGY_GROUPS,
  ...BUY_ELIGIBLE_TECHNOLOGIES.map(
    (key) => key.toUpperCase() as Uppercase<BuyEligibleTechnology>
  ),
];

export const isBuyTechnology = (v: unknown): v is Technology =>
  BUY_TECHNOLOGY_VALUES.includes(v as Technology);

export enum Ecolabel {
  NONE = "NONE",
  EKO_ENERGY = "EKO_ENERGY",
  OK_POWER = "OK_POWER",
  OK_POWER_NEW = "OK_POWER_NEW",
}

export enum Support {
  ANY = "ANY",
  SUPPORTED = "SUPPORTED",
  UNSUPPORTED = "UNSUPPORTED",
}

export enum ProductionMonth {
  OLDEST_FIRST = "OLDEST_FIRST",
  NEWEST_FIRST = "NEWEST_FIRST",
}

export enum CodRequirement {
  ANY = "ANY",
  LT_5Y = "LT_5Y",
  LT_6Y = "LT_6Y",
  LT_10Y = "LT_10Y",
  LE_15Y = "LE_15Y",
}

export enum PricingStatus {
  PRICE_FOUND = "PRICE_FOUND",
  AGENT_REACH_OUT = "AGENT_REACH_OUT",
}

export enum CancellationMode {
  COMPLIANT_AGENT = "COMPLIANT_AGENT",
  ANY_AIB_REGISTRY = "ANY_AIB_REGISTRY",
}

export interface GlobalPurchaseSettings {
  purchase_mode: PurchaseMode;
  eac_source?: string;
  technology?: Technology;
  ecolabel?: Ecolabel;
  vintage: string;
  support?: Support;
  production_month?: ProductionMonth;
  cod_requirement?: CodRequirement;
  hourly_matching: boolean;
  monthly_matching: boolean;
  /** Free-text custom sourcing requirement (OTHER mode); routes to a manual quote. */
  custom_requirement?: string;
  /** Production country of an I-REC request; unset means any I-REC country. The backend drops it for other schemes. */
  source_country?: string;
}

export interface CompanyRow {
  country: string;
  /** Buyer-picked EAC type for countries outside our registries; empty when derived or unset. */
  eac_type: string;
  volume: number;
  pricing_state: PricingState;
  touched?: boolean;
}

/**
 * State of the footer's single action button. Derived once by the orchestrator
 * so the footer's label/disabled logic and the click dispatch can't drift apart.
 */
export type BulkBuyActionState =
  "empty" | "loading" | "custom" | "review" | "ask";

/** A beneficiary row is complete (and thus eligible for pricing/submit) once every required field is filled. */
export const isBuyRowComplete = (row: CompanyRow): boolean =>
  !!(row.country && row.volume >= 1);

/** NOTE: bulk buy collects no beneficiary names, so its purpose defaults carry no {beneficiary_name} placeholder (unlike the Cancel flow's). */
export const defaultOperationNotes = (type: BeneficiaryType): string =>
  type === BeneficiaryType.ELECTRICITY_SELLER
    ? "Electricity supply"
    : "Proof of consumption";

/** NOTE: dominant scheme (first list element) for a country with an EAC scheme; undefined otherwise, never AIB-by-default. */
export const derivedEacType = (
  country: string,
  authorities: Record<string, string[]> | undefined | null
): string | undefined => (country ? authorities?.[country]?.[0] : undefined);

/** The scheme a row resolves to: the country's derived scheme wins, the buyer's manual pick is the fallback. */
export const effectiveEacType = (
  row: Pick<CompanyRow, "country" | "eac_type">,
  authorities: Record<string, string[]> | undefined | null
): string | undefined =>
  derivedEacType(row.country, authorities) ?? (row.eac_type || undefined);

/** The I-REC scheme as `registration_authorities` names it (the backend's `RegistrationAuthority.IREC`). */
export const IREC_REGISTRATION_AUTHORITY = "I-REC";

/** Every country `authorities` lists the scheme for, e.g. the countries an I-REC can be issued in. */
export const countriesWithEacType = (
  authorities: Record<string, string[]> | undefined | null,
  eacType: string
): string[] =>
  Object.entries(authorities ?? {})
    .filter(([, eacTypes]) => eacTypes.includes(eacType))
    .map(([country]) => country);

export const eacTypeLabel = (
  eacType: string,
  labels: Record<string, string> | undefined | null
): string => labels?.[eacType] ?? eacType;

/**
 * Calendar date of a quote's market data exactly as recorded (the UTC date in the ISO
 * timestamp) — local Date parsing would roll a midnight-UTC mark back a day in
 * timezones west of UTC.
 */
export const marketDataDateLabel = (priceDate: string): string =>
  formatDate(priceDate.slice(0, 10));

export interface PricingStateIdle {
  status: "idle";
}

export interface PricingStateLoading {
  status: "loading";
  message: string;
  iteration: number;
}

export interface PricingStateSuccess {
  status: "success";
  price: number;
  /** As-of timestamp of the market price level this quote is based on. */
  price_date?: string;
  pricing_request_id?: string;
  base_price?: number;
  brokerage_fee?: number;
  premiums?: Record<string, number>;
  cancellation_fee?: number;
  minimum_cancellation_fee?: number;
  go_cost_per_mwh?: number;
  mwh_cost?: number;
  cancellation_cost?: number;
  cancellation_supported?: boolean;
}

export interface PricingStateAgentReachOut {
  status: "agent_reach_out";
  pricing_request_id?: string;
}

export interface PricingStateError {
  status: "error";
  message: string;
}

export type PricingState =
  | PricingStateIdle
  | PricingStateLoading
  | PricingStateSuccess
  | PricingStateAgentReachOut
  | PricingStateError;

export interface CompanyBuyPricingRequest {
  volume: number;
  country?: string;
}

export interface BulkBuyPricingRequest {
  companies: CompanyBuyPricingRequest[];
  global_settings: GlobalPurchaseSettings;
}

export interface CompanyPricingResponse {
  volume: number;
  status: PricingStatus;
  /** As-of timestamp of the market price level this quote is based on. */
  price_date?: string | null;
  base_price?: number;
  brokerage_fee?: number;
  premiums?: Record<string, number>;
  go_cost_per_mwh?: number;
  mwh_cost?: number;
  cancellation_fee?: number;
  minimum_cancellation_fee?: number;
  cancellation_cost?: number;
  total_price?: number;
  cancellation_supported?: boolean;
}

export interface BuyPricingRequest {
  volume: number;
  country?: string;
  include_cancellation: boolean;
  global_settings: GlobalPurchaseSettings;
}

export interface BuyPricingResponse {
  status: PricingStatus;
  total_price?: number;
  /** As-of timestamp of the market price level this quote is based on. */
  price_date?: string | null;
  request_id?: string;
}

export interface BulkBuyPricingResponse {
  company_pricing: CompanyPricingResponse[];
  total_known_volume: number;
  total_known_price: number;
  agent_reach_out_count: number;
  /** Correlation id of the pricing request, shown to the buyer as the quote reference. */
  request_id?: string | null;
}

export interface CompanyBuyAndCancelOrder {
  volume: number;
  residency: string;
  eac_type?: string;
  /** Correlation id of the pricing request this row was quoted under — the buyer-visible quote reference. */
  pricing_request_id?: string;
  quoted_base_price?: number;
  quoted_brokerage_fee?: number;
  quoted_premiums?: Record<string, number>;
  quoted_cancellation_fee?: number;
  quoted_minimum_cancellation_fee?: number;
  quoted_total_price?: number;
  cancellation_mode?: CancellationMode;
}

export interface GlobalCancellationSettings {
  period_start: string;
  period_end: string;
  beneficiary_type: BeneficiaryType;
  operation_notes: string;
}

export interface BulkBuyAndCancelOrderRequest {
  orders: CompanyBuyAndCancelOrder[];
  global_settings: GlobalPurchaseSettings;
  cancellation_settings: GlobalCancellationSettings;
  payer_company_id?: number;
}

export interface OrderResult {
  success: boolean;
  order_id?: number;
  error?: string;
}

export interface BulkBuyOrderResponse {
  results: OrderResult[];
  total_successful: number;
  total_failed: number;
}

export interface UseBulkBuyPricingReturn {
  isLoadingAny: Ref<boolean>;
  fetchPricing: (rowIndex: number) => Promise<void>;
  clearPricingState: (rowIndex: number) => void;
  fetchAllPricing: () => Promise<void>;
  canSubmitOrder: Ref<boolean>;
}

export interface SelectOption {
  value: string;
  label: string;
  premium?: number;
}

export interface EacPriceData {
  vintage: string;
  bid?: string | null;
  ask?: string | null;
}

export interface BulkBuyFormOptions {
  base_prices: EacPriceData[];
  purchase_modes: SelectOption[];
  eac_sources: SelectOption[];
  technologies: SelectOption[];
  ecolabels: SelectOption[];
  supports: SelectOption[];
  production_months: SelectOption[];
  cod_requirements: SelectOption[];
  beneficiary_types: SelectOption[];
  cancellation_purposes: SelectOption[];
  vintages: number[];
  registration_authorities: Record<string, string[]>;
  selectable_eac_types: SelectOption[];
  eac_type_labels: Record<string, string>;
}
